import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Ban,
  KeyRound,
  MoreHorizontal,
  RefreshCcw,
  Send,
  Trash2,
  UserCheck,
  UserMinus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmDialog } from "./ConfirmDialog";
import { authClient } from "@/lib/auth";
import { api } from "@/lib/api";
import type { AdminUser } from "@/types";

// Impersonation is deliberately not offered: the dashboard only calls
// /api/admin/*, which rejects an impersonated non-admin session, so it would
// lock the admin out with no way back.
type Action = null | "ban" | "unban" | "delete" | "reset-data" | "reset-onboarding" | "set-password" | "send-push";

export function UserActions({
  user,
  variant = "icon",
  onChange,
}: {
  user: AdminUser;
  variant?: "icon" | "buttons";
  onChange?: () => void;
}) {
  const [action, setAction] = useState<Action>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["user", user.id] });
    qc.invalidateQueries({ queryKey: ["users"] });
    qc.invalidateQueries({ queryKey: ["overview"] });
    onChange?.();
  };

  const wrap = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      const warning = await fn();
      if (typeof warning === "string") toast.warning(warning);
      else toast.success(label);
      refresh();
      setAction(null);
    } catch (err) {
      toast.error(`${label} failed`, { description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setBusy(false);
    }
  };

  const buttons = (
    <>
      <Button variant="outline" size="sm" onClick={() => setAction("send-push")}>
        <Send className="h-3.5 w-3.5" /> Send push
      </Button>
      {user.banned ? (
        <Button variant="outline" size="sm" onClick={() => setAction("unban")}>
          <UserCheck className="h-3.5 w-3.5" /> Unban
        </Button>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setAction("ban")}>
          <Ban className="h-3.5 w-3.5" /> Ban
        </Button>
      )}
      <Button variant="outline" size="sm" onClick={() => setAction("reset-onboarding")}>
        <RefreshCcw className="h-3.5 w-3.5" /> Reset onboarding
      </Button>
      <Button variant="destructive" size="sm" onClick={() => setAction("delete")}>
        <Trash2 className="h-3.5 w-3.5" /> Delete
      </Button>
    </>
  );

  return (
    <>
      {variant === "buttons" ? (
        <div className="flex flex-wrap gap-2">{buttons}</div>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={`Actions for ${user.email}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuLabel>Manage user</DropdownMenuLabel>
            <DropdownMenuItem onSelect={() => setAction("send-push")}>
              <Send className="h-4 w-4" /> Send push
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setAction("reset-onboarding")}>
              <RefreshCcw className="h-4 w-4" /> Reset onboarding
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setAction("set-password")}>
              <KeyRound className="h-4 w-4" /> Set password
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {user.banned ? (
              <DropdownMenuItem onSelect={() => setAction("unban")}>
                <UserCheck className="h-4 w-4" /> Unban
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={() => setAction("ban")} destructive>
                <UserMinus className="h-4 w-4" /> Ban
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => setAction("reset-data")} destructive>
              <RefreshCcw className="h-4 w-4" /> Reset all data
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setAction("delete")} destructive>
              <Trash2 className="h-4 w-4" /> Delete user
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Unban */}
      <ConfirmDialog
        open={action === "unban"}
        onOpenChange={(o) => !o && setAction(null)}
        title="Unban user"
        description={`Restore access for ${user.email}?`}
        confirmText="Unban"
        busy={busy}
        onConfirm={() =>
          wrap("User unbanned", async () => {
            const res = await authClient.admin.unbanUser({ userId: user.id });
            if ("error" in res && res.error) throw new Error(res.error.message ?? "Failed");
          })
        }
      />

      {/* Reset onboarding */}
      <ConfirmDialog
        open={action === "reset-onboarding"}
        onOpenChange={(o) => !o && setAction(null)}
        title="Reset onboarding"
        description={`Force ${user.email} through the onboarding flow on next login.`}
        confirmText="Reset onboarding"
        busy={busy}
        onConfirm={() =>
          wrap("Onboarding reset", () => api.post(`/api/admin/users/${user.id}/reset-onboarding`, {}))
        }
      />

      {/* Reset all data — dangerous */}
      <ConfirmDialog
        open={action === "reset-data"}
        onOpenChange={(o) => !o && setAction(null)}
        title="Reset all user data"
        description="This wipes expenses, recurring, income, budgets, goals, trips, savings pots, device tokens, notification and message history for this user, and sends them back through onboarding. The account itself is preserved."
        tone="danger"
        confirmText="Wipe data"
        typeToConfirm={user.email}
        busy={busy}
        onConfirm={() => wrap("Data reset", () => api.post(`/api/admin/users/${user.id}/reset-data`, {}))}
      />

      {/* Delete user — dangerous */}
      <ConfirmDialog
        open={action === "delete"}
        onOpenChange={(o) => !o && setAction(null)}
        title="Permanently delete user"
        description="This action is irreversible. It removes the account, its sessions and sign-in methods, all financial data (expenses, recurring, income, budgets, goals, trips, savings pots), device tokens, integrations, feedback, subscription records and logs, then deletes their receipts and photos and revokes connected Google Sheets access."
        tone="danger"
        confirmText="Delete forever"
        typeToConfirm={`DELETE ${user.email}`}
        busy={busy}
        onConfirm={() =>
          wrap("User deleted", async () => {
            await api.post(`/api/admin/users/${user.id}/delete`, {});
            navigate("/users");
          })
        }
      />

      {/* Ban with reason + duration */}
      <BanDialog
        open={action === "ban"}
        onOpenChange={(o) => !o && setAction(null)}
        user={user}
        busy={busy}
        onSubmit={(reason, days) =>
          wrap("User banned", async () => {
            const res = await authClient.admin.banUser({
              userId: user.id,
              banReason: reason,
              banExpiresIn: days ? days * 24 * 60 * 60 : undefined,
            });
            if ("error" in res && res.error) throw new Error(res.error.message ?? "Failed");
          })
        }
      />

      {/* Send push */}
      <SendPushDialog
        open={action === "send-push"}
        onOpenChange={(o) => !o && setAction(null)}
        user={user}
        busy={busy}
        onSubmit={(title, body) =>
          wrap("Push sent", async () => {
            const res = await api.post<{ ok: boolean; sent: number }>(`/api/admin/users/${user.id}/send-push`, { title, body, type: "admin" });
            // Returning a string shows it as a warning instead of the success toast.
            if (!res.sent) return "No device received the push: this user has no working iOS device token.";
            return undefined;
          })
        }
      />

      {/* Set password */}
      <SetPasswordDialog
        open={action === "set-password"}
        onOpenChange={(o) => !o && setAction(null)}
        user={user}
        busy={busy}
        onSubmit={(pw) =>
          wrap("Password set", async () => {
            const res = await authClient.admin.setUserPassword({ userId: user.id, newPassword: pw });
            if ("error" in res && res.error) throw new Error(res.error.message ?? "Failed");
          })
        }
      />
    </>
  );
}

function BanDialog({
  open,
  onOpenChange,
  user,
  onSubmit,
  busy,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  user: AdminUser;
  onSubmit: (reason: string, days: number | null) => void;
  busy?: boolean;
}) {
  const [reason, setReason] = useState("");
  const [days, setDays] = useState<string>("");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ban {user.email}</DialogTitle>
          <DialogDescription>
            They will be signed out and prevented from logging back in. Leave duration blank for indefinite.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="ban-reason">Reason</Label>
            <Textarea id="ban-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. spam, abuse, ToS violation" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ban-days">Duration (days, optional)</Label>
            <Input id="ban-days" type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)} placeholder="indefinite" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={!reason.trim() || busy} onClick={() => onSubmit(reason.trim(), days ? Number(days) : null)}>
            {busy ? "Banning..." : "Ban user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SendPushDialog({
  open,
  onOpenChange,
  user,
  onSubmit,
  busy,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  user: AdminUser;
  onSubmit: (title: string, body: string) => void;
  busy?: boolean;
}) {
  const [title, setTitle] = useState("BillyPocket");
  const [body, setBody] = useState("");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send push to {user.email}</DialogTitle>
          <DialogDescription>Sends an APNs notification to all of this user's devices.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="push-title">Title</Label>
            <Input id="push-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="push-body">Body</Label>
            <Textarea id="push-body" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Hello from admin" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button disabled={!body.trim() || busy} onClick={() => onSubmit(title.trim(), body.trim())}>
            {busy ? "Sending..." : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SetPasswordDialog({
  open,
  onOpenChange,
  user,
  onSubmit,
  busy,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  user: AdminUser;
  onSubmit: (pw: string) => void;
  busy?: boolean;
}) {
  const [pw, setPw] = useState("");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set password for {user.email}</DialogTitle>
          <DialogDescription>Use only for support cases. The user will not be notified.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="pw">New password</Label>
          <Input id="pw" type="text" value={pw} onChange={(e) => setPw(e.target.value)} className="font-mono" autoComplete="off" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button disabled={pw.length < 8 || busy} onClick={() => onSubmit(pw)}>
            Set password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
